import React, { useState } from 'react';
import { Download, FileText, X, CheckCircle } from 'lucide-react';
import Button from '../../../components/ui/Button';

const ExportPanel = ({ data, metrics, onClose }) => {
  const [exportFormat, setExportFormat] = useState('pdf');
  const [includeCharts, setIncludeCharts] = useState(true);
  const [includeDetails, setIncludeDetails] = useState(true);
  const [isExporting, setIsExporting] = useState(false);
  const [exportSuccess, setExportSuccess] = useState(false);

  const handleExport = () => {
    setIsExporting(true);
    
    // Simulate export process
    setTimeout(() => {
      setIsExporting(false);
      setExportSuccess(true);
      
      // Generate export data
      if (exportFormat === 'excel') {
        generateExcelExport();
      } else {
        generatePDFExport();
      }
      
      // Reset success message after 3 seconds
      setTimeout(() => {
        setExportSuccess(false);
        onClose();
      }, 2000);
    }, 1500);
  };

  const generatePDFExport = () => {
    // In a real implementation, this would use a library like jsPDF
    const reportContent = generateReportContent();
    const blob = new Blob([reportContent], { type: 'application/pdf' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `relatorio-analytics-${new Date()?.toISOString()?.split('T')?.[0]}.pdf`;
    link?.click();
    URL.revokeObjectURL(url);
  };

  const generateExcelExport = () => {
    // Generate CSV format (Excel-compatible)
    const csvContent = generateCSVContent();
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `relatorio-analytics-${new Date()?.toISOString()?.split('T')?.[0]}.csv`;
    link?.click();
    URL.revokeObjectURL(url);
  };

  const generateReportContent = () => {
    return `
RELATÓRIO DE ANÁLISE FINANCEIRA - CTOBRA
Data: ${new Date()?.toLocaleDateString('pt-BR')}

=== MÉTRICAS GERAIS ===
Receita Total: ${formatCurrency(metrics?.totalRevenue)}
Lucro Líquido: ${formatCurrency(metrics?.totalProfit)}
Margem Média: ${metrics?.avgMargin?.toFixed(1)}%
Despesas Totais: ${formatCurrency(metrics?.totalExpenses)}
Total de Projetos: ${metrics?.projectCount}

${includeDetails ? generateDetailsSection() : ''}
    `;
  };

  const generateDetailsSection = () => {
    let details = '=== DETALHES DOS PROJETOS ===\n\n';
    data?.forEach((project, index) => {
      details += `${index + 1}. ${project?.projectName}\n`;
      details += `   Cliente: ${project?.userName}\n`;
      details += `   Receita: ${formatCurrency(project?.totalObra)}\n`;
      details += `   Lucro: ${formatCurrency(project?.lucroLiquido)}\n`;
      details += `   Margem: ${project?.lucroPercent?.toFixed(1)}%\n\n`;
    });
    return details;
  };

  const generateCSVContent = () => {
    let csv = 'Projeto,Cliente,Receita,Despesas,Lucro,Margem (%),Data\n';
    data?.forEach(project => {
      csv += `"${project?.projectName}",`;
      csv += `"${project?.userName}",`;
      csv += `${project?.totalObra},`;
      csv += `${project?.totalGastos},`;
      csv += `${project?.lucroLiquido},`;
      csv += `${project?.lucroPercent?.toFixed(2)},`;
      csv += `${new Date(project?.createdAt)?.toLocaleDateString('pt-BR')}\n`;
    });
    return csv;
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    })?.format(value || 0);
  };

  return (
    <div className="bg-card border border-border rounded-lg p-4 sm:p-6 mb-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <FileText className="h-5 w-5 text-primary" />
          <h3 className="text-lg font-semibold text-foreground">Exportar Relatório</h3>
        </div>
        <button
          onClick={onClose}
          className="p-1 hover:bg-muted rounded-lg transition-colors"
        >
          <X className="h-5 w-5 text-muted-foreground" />
        </button>
      </div>

      {exportSuccess ? (
        <div className="flex items-center justify-center gap-3 py-8">
          <CheckCircle className="h-8 w-8 text-success" />
          <p className="text-lg font-semibold text-success">Relatório exportado com sucesso!</p>
        </div>
      ) : (
        <div className="space-y-4">
          {/* Format Selection */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Formato de Exportação
            </label>
            <div className="flex gap-3">
              <button
                onClick={() => setExportFormat('pdf')}
                className={`flex-1 px-4 py-3 border rounded-lg transition-colors ${
                  exportFormat === 'pdf' ?'border-primary bg-primary/10 text-primary' :'border-border hover:bg-muted'
                }`}
              >
                <FileText className="h-5 w-5 mx-auto mb-1" />
                <p className="text-sm font-medium">PDF</p>
              </button>
              <button
                onClick={() => setExportFormat('excel')}
                className={`flex-1 px-4 py-3 border rounded-lg transition-colors ${
                  exportFormat === 'excel' ?'border-primary bg-primary/10 text-primary' :'border-border hover:bg-muted'
                }`}
              >
                <Download className="h-5 w-5 mx-auto mb-1" />
                <p className="text-sm font-medium">Excel (CSV)</p>
              </button>
            </div>
          </div>

          {/* Export Options */}
          <div className="space-y-3">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={includeCharts}
                onChange={(e) => setIncludeCharts(e?.target?.checked)}
                className="w-4 h-4 text-primary border-border rounded focus:ring-primary"
              />
              <span className="text-sm text-foreground">Incluir gráficos e visualizações</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={includeDetails}
                onChange={(e) => setIncludeDetails(e?.target?.checked)}
                className="w-4 h-4 text-primary border-border rounded focus:ring-primary"
              />
              <span className="text-sm text-foreground">Incluir detalhes dos projetos</span>
            </label>
          </div>

          {/* Export Summary */}
          <div className="bg-muted/50 rounded-lg p-4">
            <p className="text-sm text-muted-foreground mb-2">Resumo do Relatório:</p>
            <ul className="text-sm space-y-1">
              <li className="text-foreground">• {metrics?.projectCount} projetos incluídos</li>
              <li className="text-foreground">• Receita total: {formatCurrency(metrics?.totalRevenue)}</li>
              <li className="text-foreground">• Formato: {exportFormat?.toUpperCase()}</li>
            </ul>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3 pt-2">
            <Button
              variant="outline"
              onClick={onClose}
              disabled={isExporting}
              className="flex-1"
            >
              Cancelar
            </Button>
            <Button
              variant="default"
              onClick={handleExport}
              loading={isExporting}
              iconName="Download"
              iconPosition="left"
              className="flex-1"
            >
              {isExporting ? 'Exportando...' : 'Exportar'}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ExportPanel;